import React, { useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import { useTranslate } from "src/locales";
import { useSnackbar } from "notistack";

interface WarningModalProps {
  open: boolean;
  onClose: () => void;
  message: string;
  title?: string;
  hasReadyMadeText?: boolean;
  onConfirm: (note: string) => Promise<any> | void;
  confirmLabel?: string;
  cancelLabel?: string;
}

const WarningModal: React.FC<WarningModalProps> = ({
  open,
  onClose,
  message,
  title,
  hasReadyMadeText = false,
  onConfirm,
  confirmLabel,
  cancelLabel,
}) => {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();

  const effectiveTitle = title || t("warning-modal.title", "default", { defaultValue: "Uyarı Mesajı" });
  const effectiveConfirmLabel = confirmLabel || t("common.yes", "default", { defaultValue: "Evet" });
  const effectiveCancelLabel = cancelLabel || t("common.no", "default", { defaultValue: "Hayır" });
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  // Close handler: reset state
  const handleClose = () => {
    setNote("");
    setLoading(false);
    onClose();
  };

  const handleConfirm = async () => {
    if (!note.trim()) {
      enqueueSnackbar(
        t("warning-modal.note-required", "default", {
          defaultValue: "Silme işlemi yapabilmek için açıklama girilmesi zorunludur.",
        }),
        { variant: "warning" }
      );
      return;
    }

    setLoading(true);
    try {
      await onConfirm(note);
      handleClose();
    } catch (error) {
      console.error("Warning modal action error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          padding: 0,
          overflow: "hidden",
        },
      }}
    >
      <Box sx={{ p: "20px", display: "flex", flexDirection: "column", gap: "20px" }}>
        
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Box sx={{ width: 28, height: 28, position: "relative" }}>
             <img src="/assets/icons/modal/Alert.svg" alt="Alert" style={{ width: "100%", height: "100%" }} />
          </Box>
          <Typography
            sx={{
              flex: 1,
              color: "#333333",
              fontSize: "20px",
              fontFamily: "Poppins",
              fontWeight: 700,
            }}
          >
            {effectiveTitle}
          </Typography>
          <IconButton onClick={handleClose} disabled={loading} size="small">
             <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ height: 0, borderBottom: "1px solid #E1E1E1" }} />

        {/* Message */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <Typography
            sx={{
              color: "#333333",
              fontSize: "17px",
              fontFamily: "Poppins",
              fontWeight: 400,
            }}
          >
            {message}
          </Typography>
        </Box>

        {/* Optional Ready Text Button */}
        {hasReadyMadeText && (
          <>
            <Box sx={{ height: 0, borderBottom: "1px solid #E1E1E1" }} />
             <Box sx={{ display: "flex", alignItems: "center", gap: "15px" }}>
                <Button
                  variant="outlined"
                  sx={{
                    borderRadius: "20px",
                    textTransform: "capitalize",
                    borderColor: "#2979C3",
                    color: "#2979C3",
                    bgcolor: "rgba(99, 180, 255, 0.20)",
                    "&:hover": {
                      borderColor: "#2979C3",
                      bgcolor: "rgba(99, 180, 255, 0.30)",
                    },
                    padding: "4px 10px",
                    gap: "8px",
                  }}
                  startIcon={
                    <Box sx={{ width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <img src="/assets/icons/modal/Masked Icon.svg" alt="Add" style={{ width: "100%", height: "100%" }} />
                    </Box>
                  }
                >
                   <Typography sx={{ fontSize: "13px", fontFamily: "Poppins", fontWeight: 400 }}>
                      {t("warning-modal.add-ready-text", "default", { defaultValue: "Hazır Metin Ekle" })}
                   </Typography>
                </Button>
             </Box>
          </>
        )}

        <Box sx={{ height: 0, borderBottom: "1px solid #E1E1E1" }} />

        {/* Note Input */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <Box sx={{ display: "flex" }}>
            <Typography sx={{ fontSize: "14px", fontFamily: "Poppins", fontWeight: 400, color: "black" }}>
              {t("warning-modal.note-label", "default", { defaultValue: "Açıklama" })}
            </Typography>
            <Typography sx={{ fontSize: "14px", fontFamily: "Poppins", fontWeight: 400, color: "#FF0000", ml: 0.5 }}>
              *
            </Typography>
          </Box>
          <Typography sx={{ fontSize: "12px", fontFamily: "Poppins", fontWeight: 275, color: "rgba(0, 0, 0, 0.87)" }}>
            {t("warning-modal.note-description", "default", { defaultValue: "Silme işlemi yapabilmek için açıklama girilmesi zorunludur." })}
          </Typography>
          <ByzMuiTextField
            fullWidth
            variant="standard"
            value={note}
            placeholder=""
            onChange={(e) => setNote(e.target.value)}
            InputProps={{
               disableUnderline: false,
            }}
            sx={{
               "& .MuiInput-underline:before": { borderBottomColor: "black" },
               "& .MuiInput-underline:after": { borderBottomColor: "#2979C3" },
            }}
          />
        </Box>

        {/* Buttons */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "16px", mt: 2 }}>
           <Button
             onClick={handleClose}
             disabled={loading}
             sx={{
               height: "44px",
               padding: "0 14px",
               bgcolor: "#1E88E5",
               color: "white",
               borderRadius: "6px",
               fontSize: "14px",
               fontFamily: "Poppins",
               fontWeight: 400,
               textTransform: "none",
               "&:hover": {
                 bgcolor: "#1565C0",
               },
             }}
           >
             {effectiveCancelLabel}
           </Button>
           <Button
             onClick={handleConfirm}
             disabled={loading}
             sx={{
               height: "44px",
               padding: "0 14px",
               bgcolor: "transparent",
               color: "#2F80ED",
               border: "1px solid #2F80ED",
               borderRadius: "6px",
               fontSize: "14px",
               fontFamily: "Poppins",
               fontWeight: 400,
               textTransform: "none",
               "&:hover": {
                 bgcolor: "rgba(47, 128, 237, 0.04)",
               },
             }}
           >
             {loading ? <CircularProgress size={20} /> : effectiveConfirmLabel}
           </Button>
        </Box>

      </Box>
    </Dialog>
  );
};

export default WarningModal;
